import { memo } from "react";
import { stripMathDelimiters } from "@ever-tutor/et-ui";

import { renderCachedCaptionMath } from "@/features/tutor/utils/captionMathCache";
import type { TrailSegment } from "@/features/tutor/types/chatTrail";

const MathText = ({
  segments,
  revealed,
}: {
  segments: TrailSegment[];
  revealed?: number;
}) => {
  const limit = revealed ?? Infinity;
  let offset = 0;

  return (
    <>
      {segments.map((segment, index) => {
        const start = offset;
        offset += segment.text.length;

        if (segment.isMath) {
          // The chunker can flag a token as maths without delimiting it, so
          // fall back to the raw text and let KaTeX decide. A null render
          // means it could not parse, and the source is shown instead.
          const stripped = stripMathDelimiters(segment.text.trim());
          const html = renderCachedCaptionMath(
            stripped?.latex ?? segment.text.trim(),
          );
          const body = html ? (
            <span
              className="katex-caption"
              // safe: renderCaptionMath sanitizes via DOMPurify
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : (
            segment.text.trim()
          );

          return (
            <span
              key={index}
              className={start >= limit ? "invisible" : undefined}
            >
              {segment.text.startsWith(" ") ? " " : null}
              {body}
              {html ? stripped?.trailing : null}
            </span>
          );
        }

        const cut = Math.max(0, Math.min(segment.text.length, limit - start));
        if (cut >= segment.text.length) {
          return <span key={index}>{segment.text}</span>;
        }

        return (
          <span key={index}>
            {segment.text.slice(0, cut)}
            <span className="invisible">{segment.text.slice(cut)}</span>
          </span>
        );
      })}
    </>
  );
};

export default memo(MathText);
